import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone, Star, CheckCircle } from "@phosphor-icons/react";
import { KineticHeading } from "@/components/site/KineticHeading";
import { BrandMarquee } from "@/components/site/Marquee";
import { Reveal } from "@/components/site/Reveal";
import { SERVICES, MANIFESTO, BENEFITS, BUSINESS, IMAGES } from "@/lib/data";
import { useReviews } from "@/lib/useReviews";

const Stars = ({ n = 5, className = "" }) => (
  <div className={`flex gap-0.5 ${className}`}>
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} size={16} weight="fill" className="text-amber-400" />
    ))}
  </div>
);

export default function Home() {
  const { reviews, rating, reviewCount } = useReviews();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden bg-navy-900 grain">
        <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
          <img src={IMAGES.hero} alt="Finished basement remodel with custom staircase and epoxy floor" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/80 to-navy-900/40" />
        </motion.div>

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-5 md:px-10 pb-16 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/15 px-4 py-1.5 text-sm font-semibold text-white">
              <Stars /> {rating} · {reviewCount} Google Reviews
            </span>
          </motion.div>

          <KineticHeading
            lines={["Handyman services", "you can actually", "trust."]}
            className="font-display font-black text-white text-[13vw] leading-[1.02] tracking-tighter sm:text-7xl lg:text-8xl"
          />

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="mt-6 max-w-xl text-lg text-slate-300"
          >
            From TV mounting and furniture assembly to fences and repairs all over your home —{" "}
            {BUSINESS.short} has {BUSINESS.area} covered.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <Link
              to="/contact-us"
              data-testid="hero-quote-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-white text-navy-900 pl-7 pr-5 py-4 font-bold hover:bg-slate-100 transition-colors"
            >
              Request a Quote
              <span className="w-8 h-8 grid place-items-center rounded-full bg-navy-900 text-white group-hover:rotate-45 transition-transform">
                <ArrowUpRight size={18} weight="bold" />
              </span>
            </Link>
            <a
              href={BUSINESS.phoneHref}
              data-testid="hero-call-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white px-7 py-4 font-bold hover:bg-white/10 transition-colors"
            >
              <Phone weight="fill" size={18} /> {BUSINESS.phone}
            </a>
          </motion.div>

          <div className="mt-14 grid grid-cols-2 gap-4 max-w-lg border-t border-white/10 pt-8">
            {[
              [`${reviewCount}`, "Google reviews"],
              ["100%", "work completed"],
            ].map(([a, b], i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 + i * 0.1 }}
              >
                <div className="font-display font-black text-3xl text-white">{a}</div>
                <div className="text-sm text-slate-400">{b}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      {/* SERVICES BENTO */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24" data-testid="home-services">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-600">What we do</span>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-navy-900 mt-3 leading-[1.1]">
              One call. Every job around the house.
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="home-all-services-link"
            className="shrink-0 inline-flex items-center gap-2 font-bold text-navy-900 border-b-2 border-navy-900 pb-1"
          >
            View all services <ArrowUpRight weight="bold" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-4 auto-rows-[240px]">
          {SERVICES.slice(0, 5).map((s, i) => {
            const contain = ["fence-installation", "storage-building-assembly"].includes(s.slug);
            return (
            <Reveal
              key={s.slug}
              delay={i * 0.06}
              className={i === 0 ? "md:col-span-2 md:row-span-2" : ""}
            >
              <Link
                to="/services"
                data-testid={`home-service-${s.slug}`}
                className="group relative block w-full h-full rounded-2xl overflow-hidden border border-slate-200 bg-navy-900"
              >
                <img
                  src={s.image}
                  alt={s.title}
                  className={`absolute inset-0 w-full h-full ${contain ? "object-contain" : "object-cover"} transition-transform duration-700 group-hover:scale-105`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 via-navy-900/30 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <span className="text-xs font-semibold text-slate-300 mb-1">{s.tagline}</span>
                  <h3 className="font-display font-bold text-white text-2xl tracking-tight">{s.title}</h3>
                </div>
                <span className="absolute top-5 right-5 w-10 h-10 grid place-items-center rounded-full bg-white/90 text-navy-900 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowUpRight weight="bold" />
                </span>
              </Link>
            </Reveal>
            );
          })}
          <Reveal delay={0.3}>
            <Link
              to="/contact-us"
              data-testid="home-service-cta-card"
              className="group flex flex-col justify-between w-full h-full rounded-2xl bg-navy-900 text-white p-6"
            >
              <span className="text-sm text-slate-400">Don't see it listed?</span>
              <div>
                <h3 className="font-display font-bold text-2xl tracking-tight">We probably do that too.</h3>
                <span className="mt-3 inline-flex items-center gap-2 font-bold">
                  Just ask <ArrowUpRight weight="bold" className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-navy-900 text-white relative overflow-hidden" data-testid="home-manifesto">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-400">Why choose us</span>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3 max-w-4xl leading-[1.05] text-balance">
              A professional service, a positive attitude, and a peace of mind.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-px bg-white/10 mt-16 rounded-2xl overflow-hidden">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.1} className="bg-navy-900">
                <div className="p-8 h-full">
                  <span className="font-display font-black text-5xl text-navy-400">{m.n}</span>
                  <h3 className="font-display font-bold text-2xl tracking-tight mt-6">{m.title}</h3>
                  <p className="text-slate-400 mt-3 leading-relaxed">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS + IMAGE */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal className="order-2 lg:order-1">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-600">The promise</span>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-navy-900 mt-3 leading-[1.1]">
            No more lengthy honey-do lists.
          </h2>
          <p className="text-slate-600 mt-5 text-lg">
            More time to enjoy with family and friends — everything flows smooth without skipping a beat.
          </p>
          <ul className="mt-8 space-y-4">
            {BENEFITS.map((b, i) => (
              <li key={i} className="flex gap-3 items-start">
                <CheckCircle size={24} weight="fill" className="text-navy-600 shrink-0 mt-0.5" />
                <span className="text-navy-900 font-medium">{b}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1} className="order-1 lg:order-2">
          <div className="relative rounded-3xl overflow-hidden">
            <img src={IMAGES.honeyDo} alt="A handyman honey-do list with tasks checked off" className="w-full h-[520px] object-cover" />
          </div>
        </Reveal>
      </section>

      {/* REVIEWS */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="home-reviews">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-600">What clients say</span>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-navy-900 mt-3 leading-[1.1]">
                Rated {rating} on Google.
              </h2>
            </div>
            <Link to="/gallery" className="inline-flex items-center gap-2 font-bold text-navy-900 border-b-2 border-navy-900 pb-1">
              Read all reviews <ArrowUpRight weight="bold" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {reviews.slice(0, 3).map((r, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="bg-white rounded-2xl border border-slate-200 p-7 h-full hover:shadow-[0_12px_40px_rgb(10,17,40,0.08)] transition-shadow">
                  <Stars n={r.stars || 5} />
                  <p className="text-navy-900 mt-4 leading-relaxed font-medium">“{r.text}”</p>
                  <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
                    <span className="font-bold text-navy-900">{r.name}</span>
                    <span className="text-sm text-slate-400">{r.date}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRE-FOOTER CTA */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-28 text-center">
          <Reveal>
            <h2 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl tracking-tighter text-navy-900 leading-[1.02]">
              Ready for <br /> peace of mind?
            </h2>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/contact-us"
                data-testid="cta-book-btn"
                className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-8 py-4 font-bold hover:bg-navy-600 transition-colors"
              >
                Book a Service <ArrowUpRight weight="bold" />
              </Link>
              <a
                href={BUSINESS.phoneHref}
                className="inline-flex items-center gap-2 rounded-full border-2 border-navy-900 text-navy-900 px-8 py-4 font-bold hover:bg-navy-900 hover:text-white transition-colors"
              >
                <Phone weight="fill" /> {BUSINESS.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
